import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import {
  type Language,
  loadChineseCopy,
  localizeDataValue,
  localeFor,
  ui
} from "./lib/localization";
import { I18nContext, type I18nValue } from "./i18n-context";

export function I18nProvider({ children }: { children: ReactNode }) {
  const [language, setLanguage] = useState<Language>("en");
  // The Chinese tables load on demand, so the switch waits for them instead of
  // flashing English copy under a Chinese document language. `requested` keeps
  // repeated clicks during that load resolving to the last one pressed.
  const requested = useRef<Language>("en");
  const toggleLanguage = useCallback(() => {
    const next: Language = requested.current === "zh" ? "en" : "zh";
    requested.current = next;
    if (next === "en") {
      setLanguage("en");
      return;
    }
    void loadChineseCopy()
      .then(() => {
        if (requested.current === "zh") setLanguage("zh");
      })
      .catch(() => {
        requested.current = "en";
      });
  }, []);

  useEffect(() => {
    document.documentElement.lang = language === "zh" ? "zh-CN" : "en";
    document.documentElement.dataset.language = language;
    const description = document.querySelector<HTMLMetaElement>(
      'meta[name="description"]'
    );
    if (description) {
      description.content =
        language === "zh"
          ? "探索可搜索、标注来源的 AI 模型发布时间线，包括模型能力、可用渠道和开放性。"
          : "Explore a searchable, source-aware timeline of AI model releases, capabilities, availability, and openness.";
    }
  }, [language]);

  const value = useMemo<I18nValue>(
    () => ({
      language,
      locale: localeFor(language),
      t: (key) => ui(language, key),
      localize: (text, kind) => localizeDataValue(text, kind, language),
      toggleLanguage
    }),
    [language, toggleLanguage]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}
